import {useLayoutEffect} from 'react'
import {ConfigProvider} from 'antd'
import {themeComponentCreator} from './ComponentThemeGenerator'
import {customThemeGenerator} from './CustomTheme'
import App from './App'
import {
    BLACK_COLOR,
    BLUE_STROKE_BORDER,
    BRIGHT_GREY_COLOR,
    BRIGHT_RED_COLOR,
    DARK_MODE,
    DUST_RED_COLOR,
    GRAY_COLOR,
    GREEN_SUCCESS_ICON_COLOR,
    GUNMETAL_COLOR,
    MEDIUM_GRAY_COLOR,
    WHITE_COLOR,
} from './constants'
import { IntlProvider } from 'react-intl'
import { locale, translations } from './Locale'
import { getAntdLocale } from './i18n/AntdLocale'
import {useThemeContext} from './context/theme_context'

/**
 * Apply CSS custom properties synchronously to prevent theme-switch flicker.
 *
 * Previously this was done via two chained useEffects (setState -> setProperty),
 * which meant antd components would paint with the new theme while CSS variables
 * still had old values for 1-2 frames. useLayoutEffect runs after DOM mutation
 * but before the browser paints, so both antd tokens and CSS variables update
 * in the same visual frame.
 */
const applyCustomTheme = (mode) => {
  const cssVars = customThemeGenerator(mode)
  Object.keys(cssVars).forEach((key) => {
    const _key = key.startsWith('--') ? key : `--${key}`
    document.documentElement.style.setProperty(_key, cssVars[key])
  })
}

export const AntConfigProvider = ({ children }) => {
  const { mode, algorithm } = useThemeContext()

  return (
    <ConfigProvider
      locale={getAntdLocale(locale)}
      theme={{
        algorithm: algorithm,
        components: { ...themeComponentCreator(mode) },
        token: {
          fontFamily: 'Montserrat',
          colorBgLayout: mode == DARK_MODE ? GUNMETAL_COLOR : BRIGHT_GREY_COLOR,
          colorLink: mode == DARK_MODE ? WHITE_COLOR : BLACK_COLOR,
          colorLinkHover: mode == DARK_MODE ? MEDIUM_GRAY_COLOR : GRAY_COLOR,

          colorPrimaryBorder: BLUE_STROKE_BORDER,
          colorBorder: BLUE_STROKE_BORDER,
          colorSplit: BLUE_STROKE_BORDER,

          fontSize: 14,
          padding: 16,
          fontSizeHeading1: 14,
          fontSizeHeading2: 14,
          fontSizeHeading3: 14,
          fontSizeHeading4: 14,
          fontSizeHeading5: 14,

          colorError: mode == DARK_MODE ? DUST_RED_COLOR : BRIGHT_RED_COLOR,
          colorErrorBorder:
            mode == DARK_MODE ? DUST_RED_COLOR : BRIGHT_RED_COLOR,

          colorWarning: mode == DARK_MODE ? WHITE_COLOR : BLACK_COLOR,
          colorSuccess: mode == DARK_MODE ? GREEN_SUCCESS_ICON_COLOR : GREEN_SUCCESS_ICON_COLOR,
        },
      }}
    >
      {children}
    </ConfigProvider>
  )
}

export default function Root() {
  const { mode } = useThemeContext()

  useLayoutEffect(() => {
    applyCustomTheme(mode)
  }, [mode])

  return (
    <AntConfigProvider>
      <IntlProvider locale={locale} messages={translations}>
        {/* <App customTheme={customTheme} /> */}
        <App />
      </IntlProvider>
    </AntConfigProvider>
  )
}
